import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { User, Mail, Lock, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Register() {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', date_of_birth: '', proficiency: '' });
  const [step, setStep] = useState(1); // 1: proficiency, 2: details
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const proficiencyLevels = [
    { key: 'beginner', level: 1, title: "Wán (Beginner)", desc: "I'm new to Lamnso' or know very few words", icon: '🌱' },
    { key: 'speaker', level: 2, title: "Wà'bìn (Speaker)", desc: "I can speak some Lamnso' but want to improve reading/writing", icon: '🗣️' },
    { key: 'storyteller', level: 3, title: "Ránsísí (Storyteller)", desc: "I'm fluent in Lamnso' and can read/write well", icon: '📖' },
    { key: 'elder', level: 4, title: "Wirfon (Elder)", desc: "I'm a native speaker with deep knowledge of Lamnso' language and culture", icon: '🏛️' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await register(form);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Registration failed. Please check your connection and try again.');
    }
    setLoading(false);
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in-up">
          <Link to="/">
            <img src="https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/up9h807k_7ba9c841-3c9f-45da-b619-44a6f0b3d5bf.JPG" alt="LámFon" className="h-20 w-auto mx-auto mb-2" data-testid="register-logo" />
          </Link>
          <p className="mt-2 text-sm text-[#57534E]">Create your account and start learning</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E7E5E4] shadow-sm p-6 sm:p-8 animate-fade-in-up stagger-1">
          {error && (
            <div className="mb-4 p-3 bg-[#FEECE8] border border-red-200 rounded-xl text-sm text-red-600" data-testid="register-error">
              {error}
            </div>
          )}

          {/* Step 1: Proficiency selection */}
          {step === 1 && (
            <div>
              <h3 className="text-base font-semibold text-[#1C1917] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                What's your Lamnso' level?
              </h3>
              <p className="text-xs text-[#57534E] mb-4">This helps us personalize your experience. You can change this later.</p>
              <div className="space-y-2.5">
                {proficiencyLevels.map(p => (
                  <button key={p.key} type="button"
                    onClick={() => { updateField('proficiency', p.key); setStep(2); }}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all active:scale-[0.99] ${
                      form.proficiency === p.key ? 'border-[#4CAF50] bg-[#E8F5E9]' : 'border-[#E7E5E4] hover:border-[#D4A373]'
                    }`}
                    data-testid={`proficiency-${p.key}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{p.icon}</span>
                      <div>
                        <p className="text-sm font-semibold text-[#1C1917]">{p.title}</p>
                        <p className="text-[10px] text-[#57534E] mt-0.5">{p.desc}</p>
                        {p.key === 'elder' && (
                          <p className="text-[9px] text-[#D4A373] font-semibold mt-1">Requires admin approval for Wirfon validation privileges</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Account details */}
          {step === 2 && (
          <div>
            <button type="button" onClick={() => setStep(1)} className="flex items-center gap-1 text-xs text-[#57534E] mb-3 hover:text-[#1C1917]">
              <Calendar className="w-3 h-3" /> Back to level selection
            </button>
            <div className="flex items-center gap-2 mb-4 p-2.5 bg-[#E8F5E9] rounded-xl">
              <span className="text-lg">{proficiencyLevels.find(p => p.key === form.proficiency)?.icon}</span>
              <span className="text-xs font-semibold text-[#4CAF50]">{proficiencyLevels.find(p => p.key === form.proficiency)?.title}</span>
            </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1C1917] mb-1.5">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
                <input
                  type="text" value={form.name} onChange={(e) => updateField('name', e.target.value)}
                  className="block w-full rounded-xl border-0 py-3 pl-10 pr-4 text-[#1C1917] shadow-sm ring-1 ring-inset ring-[#E7E5E4] placeholder:text-[#A8A29E] focus:ring-2 focus:ring-inset focus:ring-[#4CAF50] sm:text-sm bg-white"
                  placeholder="Your name" required data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1C1917] mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
                <input
                  type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)}
                  className="block w-full rounded-xl border-0 py-3 pl-10 pr-4 text-[#1C1917] shadow-sm ring-1 ring-inset ring-[#E7E5E4] placeholder:text-[#A8A29E] focus:ring-2 focus:ring-inset focus:ring-[#4CAF50] sm:text-sm bg-white"
                  placeholder="your@email.com" required data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1C1917] mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
                <input
                  type="password" value={form.password} onChange={(e) => updateField('password', e.target.value)}
                  className="block w-full rounded-xl border-0 py-3 pl-10 pr-4 text-[#1C1917] shadow-sm ring-1 ring-inset ring-[#E7E5E4] placeholder:text-[#A8A29E] focus:ring-2 focus:ring-inset focus:ring-[#4CAF50] sm:text-sm bg-white"
                  placeholder="Min 6 characters" required minLength={6} data-testid="register-password-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1C1917] mb-1.5">Date of Birth</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
                <input
                  type="date" value={form.date_of_birth} onChange={(e) => updateField('date_of_birth', e.target.value)}
                  className="block w-full rounded-xl border-0 py-3 pl-10 pr-4 text-[#1C1917] shadow-sm ring-1 ring-inset ring-[#E7E5E4] focus:ring-2 focus:ring-inset focus:ring-[#4CAF50] sm:text-sm bg-white"
                  data-testid="register-dob-input"
                />
              </div>
              <p className="text-xs text-[#A8A29E] mt-1">Users under 12 have limited community access</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1C1917] mb-1.5">Gender</label>
              <div className="flex gap-2">
                {[{key: 'male', label: 'Male'}, {key: 'female', label: 'Female'}, {key: 'prefer_not_to_say', label: 'Prefer not to say'}].map(g => (
                  <button key={g.key} type="button" onClick={() => updateField('gender', g.key)}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-colors ${
                      form.gender === g.key ? 'bg-[#4CAF50] text-white' : 'bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]'
                    }`} data-testid={`gender-${g.key}`}>
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl py-3 font-semibold"
              data-testid="register-submit-button"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-5">
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E7E5E4]" /></div>
              <div className="relative flex justify-center text-xs"><span className="px-3 bg-white text-[#A8A29E]">or</span></div>
            </div>
            <button onClick={loginWithGoogle}
              className="mt-4 w-full flex items-center justify-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-[#1C1917] ring-1 ring-inset ring-[#E7E5E4] hover:bg-[#F5F5F4] transition-colors"
              data-testid="register-google-button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>
          </div>
          </div>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-[#57534E]">
          Already have an account?{' '}
          <Link to="/login" className="text-[#4CAF50] font-semibold hover:underline" data-testid="register-login-link">Sign in</Link>
        </p>

        <p className="mt-3 text-center text-[10px] text-[#A8A29E]">
          Need help? <a href="mailto:gills.fai@lamfon.app" className="text-[#4CAF50] hover:underline">Email</a> or <a href="https://wa.me/17634616615" target="_blank" rel="noopener noreferrer" className="text-[#4CAF50] hover:underline">WhatsApp</a>
        </p>
      </div>
    </div>
  );
}
